import { useEffect, useRef, useState } from 'react'

export const useSimpleCarousel = (slidesCount: number, autoplayMs = 5000) => {
  const [activeSlide, setActiveSlide] = useState(0)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const slideRefs = useRef<Array<HTMLDivElement | null>>([])

  const scrollToSlide = (index: number, behavior: ScrollBehavior = 'smooth') => {
    const track = trackRef.current
    const slide = slideRefs.current[index]

    if (!track || !slide) {
      return
    }

    track.scrollTo({ left: slide.offsetLeft, behavior })
  }

  const goToSlide = (nextIndex: number, behavior: ScrollBehavior = 'smooth') => {
    if (!slidesCount) return

    const normalized = (nextIndex + slidesCount) % slidesCount
    setActiveSlide(normalized)
    scrollToSlide(normalized, behavior)
  }

  useEffect(() => {
    if (slidesCount <= 1) return

    const intervalId = globalThis.setInterval(() => {
      goToSlide(activeSlide + 1)
    }, autoplayMs)

    return () => globalThis.clearInterval(intervalId)
  }, [activeSlide, autoplayMs, slidesCount])

  useEffect(() => {
    const track = trackRef.current

    if (!track) {
      return
    }

    const onScroll = () => {
      const nearestIndex = slideRefs.current.reduce((closestIndex, slide, index) => {
        if (!slide) {
          return closestIndex
        }

        const closestSlide = slideRefs.current[closestIndex]

        if (!closestSlide) {
          return index
        }

        const distance = Math.abs(slide.offsetLeft - track.scrollLeft)
        const closestDistance = Math.abs(closestSlide.offsetLeft - track.scrollLeft)

        return distance < closestDistance ? index : closestIndex
      }, 0)

      setActiveSlide(nearestIndex)
    }

    track.addEventListener('scroll', onScroll, { passive: true })

    return () => track.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!slidesCount) {
      setActiveSlide(0)
      return
    }

    if (activeSlide >= slidesCount) {
      setActiveSlide(0)
      scrollToSlide(0, 'auto')
    }
  }, [activeSlide, slidesCount])

  return {
    activeSlide,
    goToSlide,
    trackRef,
    slideRefs
  }
}
